<template>
  <div class="column full-width full-height no-wrap bg-white">
    <!-- ADVANCED OPTIONS DRAWER FOR BATTER PLAY -->
    <MenuSelect
      v-model="advancedDrawerOpen"
      title="Other Play Outcomes"
      :options="settingsStore.playAdvancedOutcomes"
      @select="handleAdvancedSelection"
    />

    <!-- FIELD AREA: Konva Canvas -->
    <div
      ref="containerRef"
      class="canvas-container col-grow relative-position overflow-hidden bg-grey-2"
    >
      <div class="absolute-top-left q-pa-sm" style="z-index: 10">
        <q-btn
          round
          dense
          color="white"
          text-color="primary"
          icon="undo"
          class="shadow-2 shrink"
          @click="handleUndo"
          :disable="activeSequence.length === 0"
        >
          <HelpHint text="Undo the last fielder action added to the current play." />
        </q-btn>
      </div>

      <div v-if="unifiedHistory.length > 0" class="absolute-top-right q-pa-sm" style="z-index: 10">
        <q-btn
          round
          dense
          color="primary"
          icon="history"
          class="shadow-4"
          @click="showHistoryLogSheet = true"
        >
          <q-badge color="negative" floating rounded>{{ unifiedHistory.length }}</q-badge>
        </q-btn>
      </div>

      <div
        class="absolute-bottom-left q-pa-sm"
        style="pointer-events: none; z-index: 10; max-width: 65%"
      >
        <div
          v-if="activeSequence.length > 0 || isDraftingRunnerSequence || selectedRunnerId !== null"
          class="column hide-scrollbar q-pa-xs shadow-3 transition-bg"
          :class="isDraftingRunnerSequence ? 'bg-amber-1 border-amber' : 'bg-white border-primary'"
          style="
            border-radius: 8px;
            pointer-events: auto;
            border-width: 1px;
            border-style: solid;
            opacity: 0.95;
          "
        >
          <div class="row items-center justify-between no-wrap">
            <div
              class="text-weight-bold"
              style="font-size: 0.65rem; line-height: 1"
              :class="isDraftingRunnerSequence ? 'text-amber-10' : 'text-primary'"
            >
              {{ isDraftingRunnerSequence ? 'SELECTING FIELDERS FOR:' : 'PLAY FOR:' }}
              <span
                class="text-uppercase"
                :class="isDraftingRunnerSequence ? 'text-negative' : 'text-secondary'"
              >
                {{ sequenceTargetName }}
              </span>
            </div>

            <q-btn
              v-if="isDraftingRunnerSequence"
              label="Finish"
              icon="check"
              color="positive"
              unelevated
              dense
              size="xs"
              class="q-ml-sm shadow-1 text-weight-bold q-px-xs"
              @click="stopDraftingRunnerSequence"
            />
          </div>

          <div class="row no-wrap items-center overflow-auto hide-scrollbar q-pt-xs">
            <transition-group name="sequence-list" tag="div" class="row no-wrap items-center">
              <template v-for="(item, index) in activeSequence" :key="item.id">
                <div
                  class="sequence-box row items-center justify-center cursor-pointer"
                  :class="[
                    item.isError
                      ? 'text-negative border-negative bg-white shadow-1'
                      : 'text-primary border-primary bg-white shadow-1',
                    item.role === 'missed_play' ? 'bg-grey-2 border-dashed opacity-80' : '',
                  ]"
                  @click.stop="openSequenceEdit(index)"
                >
                  {{ item.position }}
                  <span
                    v-if="item.role === 'missed_play'"
                    class="text-caption q-ml-xs text-grey-6 text-weight-medium"
                    style="font-size: 0.6rem"
                    >(Miss)</span
                  >
                  <span
                    v-else-if="item.decision && settingsStore.getDecisionWeight(item.decision) < 0"
                    class="text-caption q-ml-xs text-negative text-weight-bold"
                    style="font-size: 0.6rem"
                    >(IQ)</span
                  >
                </div>
                <q-icon
                  v-if="index < activeSequence.length - 1"
                  name="arrow_forward"
                  :color="isDraftingRunnerSequence ? 'amber-10' : 'grey-6'"
                  size="xs"
                  class="q-mx-xs"
                />
              </template>
            </transition-group>

            <div
              v-if="activeSequence.length === 0"
              class="q-py-xs text-weight-bold"
              style="font-size: 0.7rem"
              :class="isDraftingRunnerSequence ? 'text-amber-9' : 'text-grey-5'"
            >
              {{
                isDraftingRunnerSequence
                  ? 'Tap fielders to build sequence...'
                  : selectedRunnerId === null
                    ? 'Tap fielders on canvas...'
                    : 'No fielders selected.'
              }}
            </div>
          </div>
        </div>

        <q-chip
          v-else
          color="dark"
          text-color="white"
          icon="touch_app"
          size="sm"
          class="shadow-2"
          style="pointer-events: auto"
        >
          {{ tutorialContextLabel }}
        </q-chip>
      </div>

      <div class="absolute-bottom-right q-pa-sm" style="z-index: 10">
        <q-btn-toggle
          v-model="canvasMode"
          rounded
          unelevated
          toggle-color="primary"
          color="white"
          text-color="grey-8"
          :options="[
            { label: 'Defense', value: 'fielders', icon: 'shield' },
            { label: 'Runners', value: 'runners', icon: 'directions_run' },
          ]"
          class="shadow-3 text-weight-bold"
          size="sm"
        />
      </div>

      <v-stage
        class="absolute-top-left"
        v-if="stageConfig.width > 0"
        :config="{ ...stageConfig, onClick: handleEmptyCanvasTap, onTap: handleEmptyCanvasTap }"
      >
        <v-layer>
          <v-rect :config="backgroundConfig" />
          <v-wedge :config="outfieldWedgeConfig" />
          <v-wedge :config="infieldDirtConfig" />
          <v-regular-polygon :config="diamondConfig" />
          <v-regular-polygon :config="homePlateConfig" />
          <v-regular-polygon :config="firstBaseConfig" />
          <v-regular-polygon :config="secondBaseConfig" />
          <v-regular-polygon :config="thirdBaseConfig" />
          <v-line :config="retroLineLCConfig" />
          <v-line :config="retroLineRCConfig" />
          <v-arc :config="retroShortArcConfig" />

          <template v-if="gameStore.currentPitchIsHit">
            <v-line
              v-for="hit in hits"
              :key="'line-' + hit.id"
              :config="{
                opacity: 0.6,
                points: [homeAnchorX, homeAnchorY, hit.x, hit.y],
                stroke: hit.type === 'out' ? '#e74c3c' : '#3498db',
                strokeWidth: 2,
              }"
            />
            <v-circle
              v-for="hit in hits"
              :key="'marker-' + hit.id"
              :config="{
                opacity: 0.6,
                x: hit.x,
                y: hit.y,
                radius: 6,
                fill: hit.type === 'out' ? '#e74c3c' : '#3498db',
                stroke: '#2c3e50',
                strokeWidth: 1.5,
              }"
            />
          </template>

          <v-group :config="{ listening: canvasMode === 'fielders' }">
            <FieldPlayerMarker
              v-for="fielder in fielders"
              :key="'fielder-' + fielder.position"
              type="fielder"
              :uniform-number="fielder.uniformNumber"
              :position="fielder.position"
              :x="fielder.x"
              :y="fielder.y"
              :draggable="false"
              :is-selected="activeSequence.some((s) => s.position === fielder.position)"
              @select="addFielderToSequence(fielder.position)"
              @longpress="openSwapDialog(fielder.position)"
              :show-position-initials="false"
              :zone-radius="fielder.zoneRadius"
              :show-zone="true"
              :opacity="canvasMode === 'fielders' ? 1 : 0.3"
            />
          </v-group>

          <v-group :config="{ listening: canvasMode === 'runners' }">
            <FieldPlayerMarker
              v-for="runner in fieldRunners"
              :key="'runner-canvas-' + runner.id"
              type="runner"
              :uniform-number="runner.uniformNumber ?? null"
              :x="runner.x"
              :y="runner.y"
              :draggable="true"
              :is-selected="
                selectedRunnerId === runner.id ||
                (isDraftingRunnerSequence && selectedRunnerId === runner.id)
              "
              @select="handleRunnerSelect(runner.id)"
              @dragend="handleKonvaDragEnd(runner.id, $event)"
              :show-position-initials="true"
              :opacity="canvasMode === 'runners' ? 1 : 0.3"
            />
          </v-group>
        </v-layer>
      </v-stage>
      <!-- HELP MODE: long-press discovery hints, DOM overlay above the canvas -->
      <template v-if="helpModeEnabled && canvasMode === 'fielders'">
        <HelpHint
          v-for="fielder in fielders"
          :key="'help-fielder-' + fielder.position"
          :left="fielder.x + 14 + 'px'"
          :top="fielder.y - 14 + 'px'"
          icon="swap_horiz"
          label="Swap Fielder"
          text="Hold down on a fielder to swap in a different player."
        />
      </template>
    </div>
    <!-- ACTION BUTTONS: Contextual, anchored to bottom to keep it slim -->
    <q-card flat class="shrink shadow-up-2" style="z-index: 10">
      <q-card-section class="q-py-sm bg-grey-1 border-bottom" v-if="!selectedRunnerId">
        <div class="text-caption text-weight-bold text-grey-8 q-mb-xs">FINALIZE BATTER RESULT:</div>
        <div class="row q-gutter-sm q-mb-sm no-wrap">
          <q-btn
            v-for="cat in settingsStore.primaryPlayOutcomes?.filter((c) => c.visible !== false)"
            :key="cat.value"
            :label="cat.label"
            :color="cat.color"
            :text-color="cat.textColor"
            class="col text-weight-bold"
            unelevated
            padding="xs"
            :loading="isFinalizingPlay"
            :disable="isFinalizingPlay"
            :outline="
              activePlayOutcome !== cat.value ||
              settingsStore.playPrimaryOutcomesValues.includes(activePlayOutcome || '')
            "
            @click="finalizePlay(cat.value)"
          >
            <HelpHint :text="cat.tooltip || cat.label" />
          </q-btn>
          <q-btn
            icon="more_horiz"
            color="grey-8"
            class="col text-weight-bold"
            size="sm"
            padding="xs"
            unelevated
            :disable="isFinalizingPlay"
            :outline="!settingsStore.playAdvancedOutcomesValues.includes(activePlayOutcome || '')"
            @click="advancedDrawerOpen = true"
          >
            <HelpHint :text="activePlayOutcomeAdvancedLabel" />
          </q-btn>
        </div>
      </q-card-section>

      <!-- Non-hit Context (E.g. Stolen Base mid-at-bat) or if no sequence is started -->
      <q-card-section
        class="q-py-sm row q-col-gutter-xs justify-center items-center"
        v-if="activeSequence.length === 0 && !selectedRunnerId"
      >
        <div class="col-10">
          <q-btn
            label="Done (Back to Pitching)"
            icon="arrow_back"
            color="primary"
            unelevated
            size="lg"
            class="full-width"
            @click="emit('switch-view', 'pitch')"
          />
        </div>
        <div class="col-2">
          <q-btn
            icon="history"
            color="primary"
            class="full-width q-py-sm"
            unelevated
            outline
            @click="showHistoryLogSheet = true"
          >
            <q-badge v-if="unifiedHistory.length > 0" color="negative" floating rounded>{{
              unifiedHistory.length
            }}</q-badge>
          </q-btn>
        </div>
      </q-card-section>
    </q-card>

    <!-- -------------------------------------------------------- -->
    <!-- FLOATING BOTTOM SHEETS (Dialogs native z-index: 6000) -->
    <!-- -------------------------------------------------------- -->

    <!-- CONTEXTUAL FIELDER DEVELOPMENT HUD (Mechanics & IQ) -->
    <q-dialog v-model="showEditFielderSheet" position="top">
      <q-card
        class="bg-white"
        style="width: 100%; border-top-left-radius: 16px; border-top-right-radius: 16px"
      >
        <!-- Header -->
        <q-toolbar class="bg-primary text-white border-bottom">
          <q-btn
            flat
            round
            dense
            icon="chevron_left"
            :disable="(editSequenceIndex || 0) <= 0"
            @click="navigateSequence(-1)"
          />
          <q-toolbar-title class="text-subtitle1 text-weight-bold text-center">
            {{ editItemName }} <span class="text-white opacity-80">#{{ editItemUniform }}</span>
          </q-toolbar-title>
          <q-btn
            flat
            round
            dense
            icon="chevron_right"
            :disable="(editSequenceIndex || 0) >= activeSequence.length - 1"
            @click="navigateSequence(1)"
          />
          <q-btn flat round dense icon="close" v-close-popup />
        </q-toolbar>

        <!-- Scrollable content area so it never overflows tiny mobile screens -->
        <q-card-section class="q-py-sm scroll hide-scrollbar" style="max-height: 96vh">
          <!-- 1. ROLE -->
          <div class="text-caption text-weight-bold text-grey-8 q-mb-xs">Player Role</div>
          <q-btn-toggle
            v-model="editRoleState"
            spread
            no-caps
            unelevated
            toggle-color="primary"
            color="grey-2"
            text-color="grey-9"
            :options="settingsStore.primaryFielderRoles"
            @update:model-value="onEditChange"
          />
          <!-- DIFFICULTY (computed default, manually overridable) -->
          <div class="q-mt-sm">
            <div class="text-caption text-weight-bold text-grey-8 q-mb-xs">Difficulty</div>
            <q-btn-toggle
              v-model="editDifficultyState"
              spread
              no-caps
              unelevated
              toggle-color="warning"
              color="grey-2"
              text-color="grey-9"
              :options="settingsStore.playQualityOptions"
              @update:model-value="onEditChange"
              class="full-width text-weight-medium"
            />
          </div>
          <!-- 2. APPROACH (Only shown if they are the first fielder involved) -->
          <div v-if="!approachHidden" class="q-mt-sm">
            <div class="text-caption text-weight-bold text-grey-8 q-mb-xs">Approach</div>
            <div class="row q-gutter-xs wrap">
              <q-btn
                v-for="opt in approachOptions"
                :key="opt.value"
                :label="opt.label"
                :color="editApproachState === opt.value ? 'primary' : 'grey-2'"
                :text-color="editApproachState === opt.value ? 'white' : 'black'"
                unelevated
                size="sm"
                class="text-weight-bold"
                @click="onEditHandleApproach(opt.value)"
              />
            </div>
          </div>

          <!-- 3. EXECUTION -->
          <div v-if="!executionHidden" class="q-mt-sm">
            <div class="text-caption text-weight-bold text-grey-8 q-mb-xs">Execution</div>
            <div class="row q-gutter-xs wrap">
              <q-btn
                v-for="opt in executionOptions"
                :key="opt.value"
                :label="opt.label"
                :color="editExecutionState === opt.value ? 'primary' : 'grey-2'"
                :text-color="editExecutionState === opt.value ? 'white' : 'black'"
                unelevated
                size="sm"
                class="text-weight-bold"
                @click="onEditHandleExecution(opt.value)"
              />
            </div>
          </div>

          <!-- 4. THROW -->
          <div v-if="!throwHidden" class="q-mt-sm">
            <div class="text-caption text-weight-bold text-grey-8 q-mb-xs">Throw</div>
            <div class="row q-gutter-xs wrap">
              <q-btn
                v-for="opt in throwOptions"
                :key="opt.value"
                :label="opt.label"
                :color="editThrowState === opt.value ? 'primary' : 'grey-2'"
                :text-color="editThrowState === opt.value ? 'white' : 'black'"
                unelevated
                size="sm"
                class="text-weight-bold"
                @click="onEditHandleThrow(opt.value)"
              />
            </div>
          </div>

          <!-- 5. TRANSFER (only meaningful when a throw actually happened)
          <div v-if="!transferHidden" class="q-mt-sm">
            <div class="text-caption text-weight-bold text-grey-8 q-mb-xs">Transfer</div>
            <div class="row q-gutter-xs wrap">
              <q-btn
                v-for="opt in transferOptions"
                :key="opt.value"
                :label="opt.label"
                :color="editTransferState === opt.value ? 'primary' : 'grey-2'"
                :text-color="editTransferState === opt.value ? 'white' : 'black'"
                unelevated
                size="sm"
                class="text-weight-bold"
                @click="onEditHandleTransfer(opt.value)"
              />
            </div>
          </div>
          -->

          <!-- 6. ERRORS & DECISION -->
          <div class="q-mt-md">
            <div class="text-caption text-weight-bold text-grey-8 q-mb-xs">Errors & Decision</div>
            <div class="row q-gutter-sm items-center">
              <q-select
                v-model="editErrorState"
                :options="settingsStore.primaryPlayErrorStates"
                label="Scorebook Error"
                dense
                outlined
                emit-value
                map-options
                :class="decisionHidden ? 'col-12' : 'col-12 col-sm-5'"
                @update:model-value="onEditChange"
              />
              <q-select
                v-if="!decisionHidden"
                v-model="editDecisionState"
                :options="decisionOptions"
                label="Decision"
                dense
                outlined
                emit-value
                map-options
                class="col-12 col-sm-6 col-grow"
                @update:model-value="onEditHandleDecision"
              />
            </div>
          </div>
        </q-card-section>
        <q-separator />

        <!-- q-card-actions in the edit fielder sheet -->
        <q-card-actions align="between" class="bg-grey-1 q-pa-md shrink">
          <div class="row q-gutter-sm">
            <q-btn outline color="primary" icon="swap_horiz" @click="initiateSwapFromEdit">
              <q-tooltip>Swap Fielder</q-tooltip>
            </q-btn>
            <q-btn outline color="negative" icon="delete" @click="deleteFromSequence">
              <q-tooltip>Remove from play</q-tooltip>
            </q-btn>
          </div>
          <q-btn
            unelevated
            color="positive"
            label="Save & Continue"
            icon-right="check"
            @click="showEditFielderSheet = !showEditFielderSheet"
          />
        </q-card-actions>
      </q-card>
    </q-dialog>

    <!-- SHEET: FIELDER SWAP -->
    <q-dialog v-model="showSwapSheet" position="bottom">
      <q-card
        class="bg-white q-pa-md"
        style="width: 100%; border-top-left-radius: 16px; border-top-right-radius: 16px"
      >
        <div class="row justify-between items-center q-mb-sm">
          <div class="text-subtitle1 text-weight-bold text-primary">
            Swap {{ swapPositionName }}
          </div>
          <q-btn flat round dense icon="close" v-close-popup />
        </div>
        <div class="q-py-sm">
          <q-select
            v-model="selectedSwapPlayer"
            :options="availableDefensivePlayers"
            option-label="label"
            option-value="id"
            label="Available Players"
            outlined
            dense
            emit-value
            map-options
          />
        </div>
        <div class="row justify-end q-mt-md">
          <q-btn flat label="Cancel" color="grey-8" v-close-popup />
          <q-btn
            unelevated
            label="Confirm Swap"
            color="primary"
            :loading="isSwapping"
            :disable="!selectedSwapPlayer || isSwapping"
            @click="confirmSwap"
          />
        </div>
      </q-card>
    </q-dialog>

    <!-- SHEET: RUNNER WIZARD RESOLUTION -->
    <q-dialog v-model="showRunnerSheet" position="bottom">
      <q-card
        class="bg-white q-pa-md"
        style="width: 100%; border-top-left-radius: 16px; border-top-right-radius: 16px"
      >
        <div class="row justify-between items-center q-mb-md">
          <div class="text-h6 text-weight-bold text-primary">
            Runner #{{ selectedRunnerUniform }}
            <span class="text-grey-6 text-subtitle1"
              >on {{ draftStartBase === 'B' ? 'Home' : draftStartBase + 'B' }}</span
            >
          </div>
          <q-btn flat round dense icon="close" v-close-popup />
        </div>

        <!-- STEP 1: DESTINATION -->
        <div>
          <div class="text-caption text-weight-bold text-grey-7 q-mb-sm">MOVE TO</div>
          <div class="row q-gutter-sm q-mb-md">
            <q-btn
              v-for="base in availableBases"
              :key="base"
              :label="
                base === draftStartBase
                  ? 'Stay (' + base + 'B)'
                  : base === 'H'
                    ? 'Home'
                    : base + 'B'
              "
              :color="draftTargetBase === base ? 'primary' : 'grey-3'"
              :text-color="draftTargetBase === base ? 'white' : 'black'"
              unelevated
              class="col-grow text-weight-bold"
              @click="setTargetBase(base)"
            />
          </div>
        </div>

        <!-- STEP 2: RESULT -->
        <div>
          <div class="text-caption text-weight-bold text-grey-7 q-mb-sm">RESULT</div>
          <div class="row q-gutter-sm q-mb-md">
            <q-btn
              :label="draftTargetBase === 'H' ? 'Score' : 'Safe'"
              :color="draftIsOut === false ? 'positive' : 'grey-3'"
              :text-color="draftIsOut === false ? 'white' : 'black'"
              unelevated
              class="col-grow text-weight-bold"
              @click="setResult(false)"
            />
            <q-btn
              label="Out"
              :color="draftIsOut === true ? 'negative' : 'grey-3'"
              :text-color="draftIsOut === true ? 'white' : 'black'"
              unelevated
              class="col-grow text-weight-bold"
              @click="setResult(true)"
            />
          </div>
        </div>

        <!-- STEP 3: REASON & RESOLUTION -->
        <div>
          <div class="text-caption text-weight-bold text-grey-7 q-mb-sm">
            REASON
            <span v-if="!reasonTouched" class="text-grey-5 text-weight-regular">
              (defaulting to Other)</span
            >
          </div>
          <div class="row q-gutter-xs q-mb-md wrap justify-center">
            <q-btn
              v-for="r in reasonOptions"
              :key="r.value"
              :label="r.label"
              :color="draftReason === r.value ? 'primary' : 'grey-2'"
              :text-color="draftReason === r.value ? 'white' : 'black'"
              unelevated
              size="sm"
              class="col-5 text-weight-medium"
              @click="selectReason(r.value)"
            />
          </div>
          <!-- DEFENSE SUB-REASON — revealed only when Reason is Defense -->
          <div v-if="draftReason === 'DEFENSE'" class="q-mb-md">
            <div class="text-caption text-weight-bold text-grey-7 q-mb-sm">DEFENSE TYPE</div>
            <div class="row q-gutter-xs wrap justify-center">
              <q-btn
                v-for="s in defenseSubReasonOptions"
                :key="s.value"
                :label="s.label"
                :color="draftDefenseSubReason === s.value ? 'primary' : 'grey-2'"
                :text-color="draftDefenseSubReason === s.value ? 'white' : 'black'"
                unelevated
                size="sm"
                class="col-5 text-weight-medium"
                @click="selectDefenseSubReason(s.value)"
              />
            </div>
          </div>
          <!-- BASERUNNING JUMP -->
          <div class="q-mb-md">
            <div class="text-caption text-weight-bold text-grey-7 q-mb-sm">JUMP</div>
            <div class="row q-gutter-xs wrap justify-center">
              <q-btn
                v-for="j in jumpOptions"
                :key="j.value"
                :label="j.label"
                :color="draftJump === j.value ? 'primary' : 'grey-2'"
                :text-color="draftJump === j.value ? 'white' : 'black'"
                unelevated
                size="sm"
                class="col-5 text-weight-medium"
                @click="selectJump(j.value)"
              />
            </div>
          </div>
          <!-- BASERUNNING DECISION -->
          <div class="q-mb-md">
            <div class="text-caption text-weight-bold text-grey-7 q-mb-sm">DECISION</div>
            <div class="row q-gutter-xs wrap justify-center">
              <q-btn
                v-for="d in decisionOptions_runner"
                :key="d.value"
                :label="d.label"
                :color="draftDecision === d.value ? 'primary' : 'grey-2'"
                :text-color="draftDecision === d.value ? 'white' : 'black'"
                unelevated
                size="sm"
                class="col-5 text-weight-medium"
                @click="selectDecision(d.value)"
              />
            </div>
          </div>

          <!-- SHARED FIELDERS INVOLVED INDICATOR -->
          <div v-if="needsFielders" class="q-mb-md bg-grey-1 rounded-borders q-pa-sm text-center">
            <div class="text-caption text-weight-bold text-grey-7 q-mb-sm">FIELDERS INVOLVED</div>
            <div v-if="activeSequence.length === 0" class="column items-center">
              <div class="text-negative text-caption text-weight-bold q-mb-sm">
                <q-icon name="warning" /> No fielders selected!
              </div>
              <q-btn
                outline
                color="primary"
                icon="touch_app"
                label="Select Fielders on Canvas"
                size="sm"
                @click="startDraftingRunnerSequence"
              />
            </div>
            <div v-else class="column items-center">
              <div class="row justify-center items-center q-mb-sm">
                <template v-for="(item, idx) in activeSequence" :key="idx">
                  <div
                    class="q-px-sm q-py-xs bg-white border-primary text-primary text-weight-bold rounded-borders shadow-1"
                  >
                    {{ getPositionName(item.position)
                    }}<q-icon
                      v-if="item.isError"
                      name="warning"
                      color="negative"
                      size="xs"
                      class="q-ml-xs"
                    />
                  </div>
                  <q-icon
                    v-if="idx < activeSequence.length - 1"
                    name="arrow_forward"
                    color="grey-6"
                    class="q-mx-sm"
                  />
                </template>
              </div>
              <q-btn
                flat
                color="primary"
                label="Edit Fielder Sequence"
                size="sm"
                @click="startDraftingRunnerSequence"
              />
            </div>
          </div>

          <q-btn
            label="Save Runner"
            color="primary"
            class="full-width q-mt-sm"
            unelevated
            size="lg"
            :loading="isSavingRunner"
            :disable="!draftReason || !draftTargetBase || draftIsOut === null || isSavingRunner"
            @click="saveRunner"
          />
        </div>
      </q-card>
    </q-dialog>

    <!-- SHEET: UNIFIED PLAY HISTORY LOG -->
    <q-dialog v-model="showHistoryLogSheet" position="bottom">
      <q-card
        class="bg-white"
        style="width: 100%; border-top-left-radius: 16px; border-top-right-radius: 16px"
      >
        <div class="row justify-between items-center q-px-md q-pt-md q-pb-sm border-bottom">
          <div class="text-subtitle1 text-weight-bold text-primary text-uppercase">
            Play History
          </div>
          <q-btn flat round dense icon="close" v-close-popup />
        </div>
        <q-list separator dense class="q-mb-md">
          <q-item v-for="p in unifiedHistory" :key="p.id" class="q-py-md q-px-md">
            <q-item-section>
              <template v-if="p.eventType === 'PLAY'">
                <q-item-label class="text-weight-bold text-body1 text-primary">
                  Batter #{{ currentBatterUniform }}: Hit into Play
                </q-item-label>
                <q-item-label caption>
                  Result: {{ p.outcome }}
                  <span v-if="getPlaySequenceString(p)"> — {{ getPlaySequenceString(p) }}</span>
                </q-item-label>
              </template>
              <template v-else>
                <q-item-label class="text-weight-bold text-body1">
                  Runner #{{ getRunnerUniform((p as any).runnerId) }}:
                  {{ (p as any).startBase === 'B' ? 'Home' : (p as any).startBase + 'B' }} &rarr;
                  {{ (p as any).endBase === 'H' ? 'Home' : (p as any).endBase + 'B' }}
                </q-item-label>
                <q-item-label caption v-if="(p as any).advanceReason">
                  {{ getReasonLabel((p as any).advanceReason) }}
                </q-item-label>
              </template>
            </q-item-section>

            <q-item-section side>
              <q-btn
                round
                dense
                flat
                color="negative"
                icon="delete"
                aria-label="Delete this entry"
                :disable="isDeletingHistoryItem"
              >
                <q-menu anchor="top right" self="bottom right">
                  <q-card class="q-pa-sm" style="min-width: 170px">
                    <div class="text-caption q-mb-sm">Delete this entry? This can't be undone.</div>
                    <div class="row justify-end q-gutter-xs">
                      <q-btn flat dense label="Cancel" color="grey-7" v-close-popup />
                      <q-btn
                        flat
                        dense
                        label="Delete"
                        color="negative"
                        v-close-popup
                        @click="confirmDeleteHistoryItem(p)"
                      />
                    </div>
                  </q-card>
                </q-menu>
              </q-btn>
            </q-item-section>
          </q-item>
        </q-list>
      </q-card>
    </q-dialog>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, onMounted, onBeforeUnmount, computed } from 'vue'
import { useGameStore } from 'stores/game-store'
import { useSettingsStore } from 'stores/settings-store'
import { useAuthStore } from 'stores/auth-store'
import { useFieldLayout } from 'src/helpers/helpers'
import { useHelpMode } from 'src/composables/useHelpMode'
import { useAsyncGuard } from 'src/composables/useAsyncGuard'
import FieldPlayerMarker from 'components/FieldPlayerMarker.vue'
import MenuSelect from 'components/MenuSelect.vue'
import HelpHint from 'components/HelpHint.vue'
import { useNotify } from 'src/helpers/notify'
import type {
  SequenceItemRole,
  FieldingApproach,
  FieldingThrowQuality,
  RunnerAdvanceTransaction,
  PlayTransaction,
  SequenceItem,
  PlayOutcome,
  FieldingExecution,
  FieldingDecision,
  RunnerAdvanceReason,
  DefenseSubReason,
  BaserunningJump,
  BaserunningDecision,
} from 'src/stores/base-types'

const emit = defineEmits(['switch-view'])
const gameStore = useGameStore()
const settingsStore = useSettingsStore()
const authStore = useAuthStore()
const { helpModeEnabled } = useHelpMode()

const advancedDrawerOpen = ref(false)

// --- RESPONSIVE CANVAS SETUP ---
const containerRef = ref<HTMLElement | null>(null)
const stageConfig = reactive({ width: 0, height: 0 })
let resizeObserver: ResizeObserver | null = null
const stageSizeRef = computed(() => ({ width: stageConfig.width, height: stageConfig.height }))
const {
  homeAnchorX,
  homeAnchorY,
  fieldScale,
  backgroundConfig,
  outfieldWedgeConfig,
  infieldDirtConfig,
  diamondConfig,
  homePlateConfig,
  firstBaseConfig,
  secondBaseConfig,
  thirdBaseConfig,
  retroLineLCConfig,
  retroLineRCConfig,
  retroShortArcConfig,
  getStandardPositionCoords,
} = useFieldLayout(stageSizeRef)

onMounted(() => {
  if (containerRef.value) {
    resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        stageConfig.width = entry.contentRect.width
        stageConfig.height = entry.contentRect.height
      }
    })
    resizeObserver.observe(containerRef.value)
  }
  // Auto-prompt Fielder HUD if the hit plotted a specific zone
  if (
    activeEditPitch.value &&
    activeEditPitch.value.zoneFielderPosition &&
    activeSequence.value.length === 0
  ) {
    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    setTimeout(async () => {
      await addFielderToSequence(activeEditPitch.value!.zoneFielderPosition!)
    }, 150)
  }
})

onBeforeUnmount(() => {
  if (resizeObserver && containerRef.value) resizeObserver.unobserve(containerRef.value)
})

// --- CONTEXT TOGGLE STATE ---
const canvasMode = ref<'fielders' | 'runners'>('fielders')

// --- TRANSACTION BINDING (REPLACES LOCAL SEQUENCE ARRAYS) ---
const activeEditPitch = computed(() => {
  const currentTxns = gameStore.currentAtBatTransactions || []
  return (
    currentTxns.find((p) => String(p.id) === String(gameStore.editingPitchId)) ||
    currentTxns[currentTxns.length - 1]
  )
})

const getPlaySequenceString = (p: PlayTransaction | RunnerAdvanceTransaction): string => {
  if (!p.playSequence || p.playSequence.length === 0) return ''
  return p.playSequence
    .map((seq) => (seq.isError ? `${seq.position}E` : seq.position.toString()))
    .join('-')
}

// Dynamically target either the Batter's Play or the active Runner's Advance!
const activeTxnId = computed(() => {
  if (selectedRunnerId.value) {
    const rTxn = gameStore.transactions.find(
      (t) =>
        t.eventType === 'RUNNER_ADVANCE' && t.runnerId === selectedRunnerId.value && t.isIncomplete,
    )
    return rTxn?.id
  } else {
    return gameStore.activePlay?.id
  }
})

const activeSequence = computed(() => {
  const txn = gameStore.transactions.find((t) => t.id === activeTxnId.value) as
    | PlayTransaction
    | RunnerAdvanceTransaction
  return txn?.playSequence || []
})

const activePlayOutcome = computed(() => {
  return gameStore.activePlay?.outcome
})
const activePlayOutcomeAdvancedLabel = computed(() => {
  const advOption = settingsStore.playAllOutcomes.find(
    (s) => s.value === (activePlayOutcome.value || ''),
  )
  return advOption ? advOption.label : 'Other Outcome Options'
})

// --- NEW: ACTION-FIRST SEQUENCE LOGIC ---
const showEditFielderSheet = ref(false)
const editSequenceIndex = ref<number | null>(null)

// Local state for the Fielder Development HUD
const editRoleState = ref<SequenceItemRole>('fielded')
const editApproachState = ref<FieldingApproach>('routine')
const editExecutionState = ref<FieldingExecution>('clean')
//const editTransferState = ref<FieldingTransferQuality>('not_required')
const editThrowState = ref<FieldingThrowQuality>('not_required')
const editDecisionState = ref<FieldingDecision>('not_required')
const editDifficultyState = ref<number | null>(null)
const editErrorState = ref<'none' | 'fielding' | 'throwing'>('none')

// --- CONTEXTUAL UI VISIBILITY LOGIC ---
const approachOptions = computed<{ label: string; value: FieldingApproach }[]>(() => {
  const item = activeSequence.value[editSequenceIndex.value ?? -1]
  if (!item) return []
  return settingsStore.approachOptionsForPosition(
    item.position,
    item.role || 'fielded',
    activeEditPitch.value?.battedBallTrajectory || undefined,
  )
})
const executionOptions = computed<{ label: string; value: FieldingExecution }[]>(() => {
  const item = activeSequence.value[editSequenceIndex.value ?? -1]
  if (!item) return []
  return settingsStore.executionOptionsForPosition(
    item.position,
    item.role || 'fielded',
    activeEditPitch.value?.battedBallTrajectory || undefined,
  )
})
/*
const transferOptions = computed<{ label: string; value: FieldingTransferQuality }[]>(() => {
  const item = activeSequence.value[editSequenceIndex.value ?? -1]
  if (!item) return []
  return settingsStore.transferOptionsForPosition(item.position, item.role || 'fielded')
})

 */
const throwOptions = computed<{ label: string; value: FieldingThrowQuality }[]>(() => {
  const item = activeSequence.value[editSequenceIndex.value ?? -1]
  if (!item) return []
  return settingsStore.throwOptionsForPosition(item.position, item.role || 'fielded')
})
const decisionOptions = computed<{ label: string; value: FieldingDecision }[]>(() => {
  const item = activeSequence.value[editSequenceIndex.value ?? -1]
  if (!item) return []
  return settingsStore.decisionOptionsForPosition(item.position, item.role || 'fielded')
})
const approachHidden = computed(() => approachOptions.value.length === 0)
const executionHidden = computed(() => executionOptions.value.length === 0)
const throwHidden = computed(() => throwOptions.value.length === 0)
//const transferHidden = computed(() => transferOptions.value.length === 0)
const decisionHidden = computed(() => decisionOptions.value.length === 0)

const computeDefaultDifficulty = (position: number): 1 | 2 | 3 => {
  // NEW: If there is already a sequence, and this player isn't the first one in it, default to 1 (Routine)
  if (activeSequence.value.length >= 1) {
    return 1
  }
  const pitch = activeEditPitch.value
  if (!pitch || pitch.hitLocationX == null || pitch.hitLocationY == null) return 1

  const fielderCoords = getStandardPositionCoords(position)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const fielderRelX = (fielderCoords.x - homeAnchorX.value) / fieldScale.value
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const fielderRelY = (fielderCoords.y - homeAnchorY.value) / fieldScale.value
  return 1 // default to 1
  /*
  return (
    gameStore.calculateHitProximity(
      position,
      pitch.hitLocationX,
      pitch.hitLocationY,
      fielderRelX,
      fielderRelY,
    ) || 1
  )

   */
}

const addFielderToSequence = async (position: number) => {
  let targetId = activeTxnId.value

  if (!targetId) {
    if (isDraftingRunnerSequence.value && selectedRunnerId.value) {
      const rTxn = await gameStore.getOrCreateActiveRunnerAdvance(selectedRunnerId.value)
      targetId = rTxn?.id
    } else {
      const pTxn = await gameStore.getOrCreateActivePlay()
      targetId = pTxn?.id
    }
  }

  if (targetId) {
    const defaultDifficulty = computeDefaultDifficulty(position)
    await gameStore.addFielderToTransaction(targetId, position, defaultDifficulty)
    const txn = gameStore.transactions.find((t) => t.id === targetId) as
      | PlayTransaction
      | RunnerAdvanceTransaction
    if (txn) openSequenceEdit(txn.playSequence.length - 1)
  }
}

const openSequenceEdit = (index: number) => {
  editSequenceIndex.value = index
  const item = activeSequence.value[index]

  if (item) {
    editRoleState.value = item.role || 'fielded'
    editApproachState.value = approachHidden.value ? 'not_scored' : item.approach || 'routine'
    editExecutionState.value = executionHidden.value ? 'not_scored' : item.execution || 'clean'
    // editTransferState.value = transferHidden.value ? 'not_required' : item.transfer || 'not_required'
    editThrowState.value = throwHidden.value ? 'not_required' : item.throw || 'not_required'
    editDecisionState.value = decisionHidden.value
      ? 'not_required'
      : item.decision || 'not_required'
    editErrorState.value = item.isError ? item.errorType || 'fielding' : 'none'
    editDifficultyState.value = item.difficulty ?? 1
  }
  showEditFielderSheet.value = true
}
const handleUndo = async () => {
  if (canvasMode.value === 'runners') {
    await gameStore.undoLastRunner()
  } else {
    await gameStore.undoLastFielder()
  }
}
const onEditHandleApproach = async (val: string) => {
  editApproachState.value = val as FieldingApproach
  await onEditChange()
}
const onEditHandleExecution = async (val: string) => {
  editExecutionState.value = val as FieldingExecution
  await onEditChange()
}
/*
const onEditHandleTransfer = async (val: string) => {
  editTransferState.value = val as FieldingTransferQuality
  await onEditChange()
}
 */
const onEditHandleThrow = async (val: string) => {
  editThrowState.value = val as FieldingThrowQuality
  await onEditChange()
}
const onEditHandleDecision = async (val: string) => {
  editDecisionState.value = val as FieldingDecision
  await onEditChange()
}

const onEditChange = async () => {
  if (editSequenceIndex.value !== null && activeTxnId.value) {
    const updates: Partial<SequenceItem> = {
      role: editRoleState.value,
      approach: editApproachState.value,
      execution: editExecutionState.value,
      //transfer: editTransferState.value,
      throw: editThrowState.value,
      decision: editDecisionState.value,
      isError: editErrorState.value !== 'none',
      errorType: editErrorState.value !== 'none' ? editErrorState.value : null,
    }
    await gameStore.updateSequenceItem(activeTxnId.value, editSequenceIndex.value, updates)
  }
}

const getFielderZoneRadius = (position: number) => {
  const scale = fieldScale.value
  switch (position) {
    case 7:
    case 8:
    case 9:
      return scale * settingsStore.fielderZoneScale[7]!
    case 4:
      return scale * settingsStore.fielderZoneScale[6]!
    case 6:
      return scale * settingsStore.fielderZoneScale[6]!
    case 3:
      return scale * settingsStore.fielderZoneScale[3]!
    case 5:
      return scale * settingsStore.fielderZoneScale[5]!
    case 1:
      return scale * settingsStore.fielderZoneScale[1]!
    case 2:
      return scale * settingsStore.fielderZoneScale[2]!
    default:
      return scale * 0.15
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const handleEmptyCanvasTap = (e: any) => {
  if (e.target.className === 'Circle' || e.target.className === 'Text') return
  if (selectedRunnerId.value) {
    selectedRunnerId.value = null
    return
  }
  if (isDraftingRunnerSequence.value) stopDraftingRunnerSequence()
}

// --- SEQUENCE EDITING ---
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const moveSequenceItem = async (direction: number) => {
  if (editSequenceIndex.value === null || !activeTxnId.value) return
  const newIndex = editSequenceIndex.value + direction
  if (newIndex < 0 || newIndex >= activeSequence.value.length) return

  await gameStore.reorderSequence(activeTxnId.value, editSequenceIndex.value, newIndex)
  editSequenceIndex.value = newIndex // keep the sheet pointed at the item that just moved
}
const deleteFromSequence = async () => {
  if (editSequenceIndex.value !== null && activeTxnId.value) {
    await gameStore.removeSequenceItem(activeTxnId.value, editSequenceIndex.value)
  }
  showEditFielderSheet.value = false
}
const initiateSwapFromEdit = () => {
  if (editSequenceIndex.value === null) return
  const pos = activeSequence.value[editSequenceIndex.value]?.position
  showEditFielderSheet.value = false
  if (pos) openSwapDialog(pos)
}
const navigateSequence = (direction: number) => {
  if (editSequenceIndex.value === null) return
  const newIndex = editSequenceIndex.value + direction
  if (newIndex >= 0 && newIndex < activeSequence.value.length) openSequenceEdit(newIndex)
}

const editItemName = computed(() => {
  if (editSequenceIndex.value === null) return ''
  const pos = activeSequence.value[editSequenceIndex.value]?.position || -1
  return settingsStore.positionStrings[pos] || String(pos)
})
const editItemUniform = computed(() => {
  if (editSequenceIndex.value === null) return ''
  const pos = activeSequence.value[editSequenceIndex.value]?.position
  return fielders.value.find((f) => f.position === pos)?.uniformNumber || '-'
})

// Read-Only Sequence Target Banner Info
const currentBatterUniform = computed(
  () => gameStore.currentBatterEntry?.player?.uniformNumber || '-',
)

const sequenceTargetName = computed(() => {
  if (selectedRunnerId.value) return `Runner #${selectedRunnerUniform.value}`
  return `Batter #${currentBatterUniform.value}`
})

const tutorialContextLabel = computed(() => {
  let returnVal = 'Tap grass where hit landed.'
  if (isDraftingRunnerSequence.value) returnVal = 'Tap fielders involved'
  else if (selectedRunnerId.value) returnVal = 'Resolve runner outcome'
  else if (activeSequence.value.length > 0) returnVal = 'Tap fielder for relay, or finalize below.'
  return returnVal
})

// --- SWAP FIELDER STATE ---
const showSwapSheet = ref(false)
const swapPosition = ref<number | null>(null)
const selectedSwapPlayer = ref<number | null>(null)

const swapPositionName = computed(() =>
  swapPosition.value
    ? settingsStore.positionStrings[swapPosition.value] || String(swapPosition.value)
    : '',
)
const availableDefensivePlayers = computed(() => {
  const currentPA = gameStore.viewedPlateAppearance
  if (!currentPA) return []

  const lineup = currentPA.halfInning === 'top' ? gameStore.homeLineup : gameStore.visitingLineup
  const snapshot = currentPA.defenseSnapshot || {}

  return (
    lineup?.map((e) => {
      // 1. Find the player's active position in the snapshot
      let activePos = null
      for (const [posStr, id] of Object.entries(snapshot)) {
        if (id.playerId === e.player.id) {
          activePos = Number(posStr)
          break
        }
      }

      // 2. Format a clean position string
      const posLabel = activePos ? `- Pos ${activePos}` : '- Bench'

      // 3. Mark as (Current) if their snapshot position matches the one we are swapping
      const isCurrent = activePos === swapPosition.value ? '(Current)' : ''

      return {
        label: `#${e.player.uniformNumber || '-'} ${e.player.name} ${posLabel} ${isCurrent}`.trim(),
        id: e.player.id,
      }
    }) || []
  )
})
const openSwapDialog = (position: number) => {
  swapPosition.value = position
  const currentPA = gameStore.viewedPlateAppearance
  selectedSwapPlayer.value =
    currentPA && currentPA.defenseSnapshot
      ? currentPA.defenseSnapshot[position as keyof typeof currentPA.defenseSnapshot]?.playerId ||
        null
      : null
  showSwapSheet.value = true
}

const { isPending: isSwapping, guard: guardSwap } = useAsyncGuard()
const confirmSwap = guardSwap(async () => {
  if (swapPosition.value && selectedSwapPlayer.value) {
    await gameStore.swapDefensivePlayer(swapPosition.value, selectedSwapPlayer.value)
    useNotify('Fielder successfully swapped!', 1000, 'positive')
  }
  showSwapSheet.value = false
  swapPosition.value = null
  selectedSwapPlayer.value = null
})

// --- RUNNER RESOLUTION WIZARD STATE ---
const selectedRunnerId = ref<number | null>(null)
const showRunnerSheet = ref(false)
const showHistoryLogSheet = ref(false)
const isDraftingRunnerSequence = ref(false)

const draftStartBase = ref<string>('B')
const draftTargetBase = ref<string | null>(null)
const draftIsOut = ref<boolean | null>(null)

const draftReason = ref<RunnerAdvanceReason | ''>('OTHER')
const draftDefenseSubReason = ref<DefenseSubReason | ''>('')
const draftJump = ref<BaserunningJump | ''>('')
const draftDecision = ref<BaserunningDecision>('not_required')

const reasonOptions = computed(() => settingsStore.primaryRunnerAdvanceReasonsSimplified)
const defenseSubReasonOptions = computed(() => settingsStore.primaryDefenseSubReasons)
const jumpOptions = computed(() => settingsStore.primaryBaserunningJumpOptions)
const decisionOptions_runner = computed(() => settingsStore.primaryBaserunningDecisionOptions)

const needsFielders = computed(() => draftReason.value === 'DEFENSE')

const selectedRunnerUniform = computed(() => {
  if (!selectedRunnerId.value) return '-'
  return fieldRunners.value.find((r) => r.id === selectedRunnerId.value)?.uniformNumber || '-'
})

const availableBases = computed(() => {
  if (draftStartBase.value === '1') return ['1', '2', '3', 'H']
  if (draftStartBase.value === '2') return ['2', '3', 'H']
  if (draftStartBase.value === '3') return ['3', 'H']
  return ['B', '1', '2', '3', 'H']
})

const setTargetBase = (base: string) => {
  draftTargetBase.value = base
}
const setResult = (isOut: boolean) => {
  draftIsOut.value = isOut
}
const reasonTouched = ref(false)
const selectReason = (reason: RunnerAdvanceReason) => {
  draftReason.value = reason
  reasonTouched.value = true
  if (reason !== 'DEFENSE') draftDefenseSubReason.value = ''
}
const selectDefenseSubReason = (sub: DefenseSubReason) => {
  draftDefenseSubReason.value = sub
}
const selectJump = (jump: BaserunningJump) => {
  draftJump.value = jump
}
const selectDecision = (decision: BaserunningDecision) => {
  draftDecision.value = decision
}

const openRunnerSheet = async (id: number, prefilledBase: string | null = null) => {
  selectedRunnerId.value = id
  draftStartBase.value = getRunnerBase(id) || 'B'
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  await gameStore.getOrCreateActiveRunnerAdvance(id, draftStartBase.value as any)
  draftTargetBase.value = prefilledBase
  draftIsOut.value = null
  draftReason.value = 'OTHER'
  draftDefenseSubReason.value = ''
  draftJump.value = ''
  draftDecision.value = 'not_required'
  showRunnerSheet.value = true
  reasonTouched.value = false
}

const startDraftingRunnerSequence = () => {
  showRunnerSheet.value = false
  isDraftingRunnerSequence.value = true
  canvasMode.value = 'fielders' // Force focus to defense
}
const stopDraftingRunnerSequence = () => {
  isDraftingRunnerSequence.value = false
  showRunnerSheet.value = true
  canvasMode.value = 'fielders' // Force focus to defense
}

const handleRunnerSelect = async (id: number) => {
  if (isDraftingRunnerSequence.value && selectedRunnerId.value === id) {
    stopDraftingRunnerSequence()
    return
  }
  if (isDraftingRunnerSequence.value) isDraftingRunnerSequence.value = false
  await openRunnerSheet(id)
}

const { isPending: isSavingRunner, guard: guardSaveRunner } = useAsyncGuard()
const saveRunner = guardSaveRunner(async () => {
  if (!selectedRunnerId.value || !draftTargetBase.value || !draftReason.value) return

  let targetId = activeTxnId.value
  if (!targetId) {
    const rTxn = await gameStore.getOrCreateActiveRunnerAdvance(selectedRunnerId.value)
    targetId = rTxn?.id
  }

  if (targetId) {
    await gameStore.finalizeRunnerAdvance(targetId, {
      endBase: draftTargetBase.value,
      isOut: draftIsOut.value,
      runsScored: !draftIsOut.value && draftTargetBase.value === 'H' ? 1 : 0,
      advanceReason: draftReason.value,
      defenseSubReason:
        draftReason.value === 'DEFENSE' ? draftDefenseSubReason.value || null : null,
      baserunningJump: draftJump.value || undefined,
      baserunningDecision: draftDecision.value,
    })
  }

  showRunnerSheet.value = false
  selectedRunnerId.value = null
  isDraftingRunnerSequence.value = false

  // NEW: Cascade to the next unresolved lead runner!
  if (authStore.currentUser?.settings?.autoAdvanceLeadRunner !== false) {
    const nextRunnerId = getLeadRunnerExcludingBatter()
    if (nextRunnerId) {
      canvasMode.value = 'runners'
      await openRunnerSheet(nextRunnerId)
      return
    }
  }
})

const unifiedHistory = computed(() => {
  const currentPA = gameStore.viewedPlateAppearance
  if (!currentPA) return []
  return gameStore.transactions
    .filter(
      (t) =>
        t.plateAppearanceId === currentPA.id &&
        (t.eventType === 'PLAY' || t.eventType === 'RUNNER_ADVANCE') &&
        !t.isIncomplete,
    )
    .sort((a, b) => a.sequence - b.sequence) as (PlayTransaction | RunnerAdvanceTransaction)[]
})
const { isPending: isDeletingHistoryItem, guard: guardDeleteHistory } = useAsyncGuard()
const confirmDeleteHistoryItem = guardDeleteHistory(
  async (item: PlayTransaction | RunnerAdvanceTransaction) => {
    if (item.eventType === 'PLAY') {
      await gameStore.deletePlay(item.id)
    } else {
      await gameStore.deleteRunnerAdvance(item.id)
    }
  },
)

// --- GENERAL PLAY OUTCOME (BATTER) STATE ---
const getLeadRunnerExcludingBatter = () => {
  const br = gameStore.currentBaseRunners
  const currentPA = gameStore.viewedPlateAppearance
  const batterId = currentPA?.batterId

  // NEW: Find runners who have ALREADY been resolved in this plate appearance
  const resolvedRunnerIds = gameStore.transactions
    .filter(
      (t) =>
        t.plateAppearanceId === currentPA?.id &&
        t.eventType === 'RUNNER_ADVANCE' &&
        !t.isIncomplete,
    )
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    .map((t) => (t as any).runnerId)

  const checkBases = ['3', '2', '1']
  for (const base of checkBases) {
    for (const runner of br[base as keyof typeof br]) {
      // Ignore the batter AND ignore runners we already saved!
      if (runner.id !== batterId && !resolvedRunnerIds.includes(runner.id)) {
        return runner.id
      }
    }
  }
  return null
}

const handleAdvancedSelection = async (val: string) => {
  advancedDrawerOpen.value = false
  await finalizePlay(val)
}

const { isPending: isFinalizingPlay, guard: guardFinalize } = useAsyncGuard()
const finalizePlay = guardFinalize(async (val: string) => {
  const outcome = val as PlayOutcome

  // 1. Save the play outcome
  await gameStore.updatePlayOutcome(outcome)
  const outs = await gameStore.finalizeCurrentPlay()

  // 2. NEW: Automatically advance the batter based on the outcome rules!
  await gameStore.syncBatterAdvanceFromHit(val)

  useNotify(
    'Play recorded successfully!',
    1000,
    'positive',
    outs > 0 ? `Recorded ${outs} out(s).` : 'No outs recorded.',
  )

  // NEW: if this outcome already fully accounts for an out beyond the batter
  // (DP/TP), don't auto-open the runner sheet — the coach didn't select a
  // runner outcome that needs resolving, the play outcome already says it.
  const outcomeAlreadyResolvesRunnerOut = outcome === 'DP' || outcome === 'TP'

  if (
    !outcomeAlreadyResolvesRunnerOut &&
    authStore.currentUser?.settings?.autoAdvanceLeadRunner !== false
  ) {
    const leadRunnerId = getLeadRunnerExcludingBatter()
    if (leadRunnerId) {
      canvasMode.value = 'runners'
      await openRunnerSheet(leadRunnerId)
      return
    }
  }

  const br = gameStore.currentBaseRunners
  const basesEmpty =
    br['1'].length === 0 && br['2'].length === 0 && br['3'].length === 0 && br['B'].length === 0

  if (basesEmpty) emit('switch-view', 'pitch')
})

// --- DATA MAPPING ---
const getPositionName = (pos: number) => settingsStore.positionStrings[pos] || String(pos)
const getReasonLabel = (reason: string) => {
  const primary = reasonOptions.value.find((r) => r.value === reason)
  if (primary) return primary.label
  const sub = defenseSubReasonOptions.value.find((r) => r.value === reason)
  return sub?.label || reason
}

const getRunnerUniform = (id: number) => {
  const currentPA = gameStore.viewedPlateAppearance
  if (!currentPA) return '-'
  const lineup = currentPA.halfInning === 'top' ? gameStore.visitingLineup : gameStore.homeLineup
  return lineup?.find((e) => e.player.id === id)?.player.uniformNumber || '-'
}

const fielders = computed(() => {
  const fielderArray = []

  // Strictly loop 1 through 9 to guarantee every position is drawn on the canvas
  for (let pos = 1; pos <= 9; pos++) {
    const coords = getStandardPositionCoords(pos)

    // Default uniform number to the position number if no one is found
    let uniform = '--' // pos.toString()

    // 1. Check if they are explicitly assigned in the current defensive snapshot
    const snapshotFielder = gameStore.currentDefence.find((f) => f.position === pos)

    // We use != null so that if a player legitimately has uniform '0', it doesn't fail the truthy check!
    if (snapshotFielder && snapshotFielder.uniformNumber != null) {
      uniform = snapshotFielder.uniformNumber.toString()
    }

    fielderArray.push({
      uniformNumber: uniform,
      position: pos,
      x: coords.x,
      y: coords.y,
      zoneRadius: getFielderZoneRadius(pos),
    })
  }

  return fielderArray
})

const hits = computed(() => {
  return gameStore.currentHits.map((pitchTxn) => {
    const outcome = pitchTxn.outcome || pitchTxn.pitchOutcome || ''
    const isHit = ['S', 'D', 'T', 'HR', 'X'].includes(outcome)
    return {
      id: pitchTxn.id,
      x: homeAnchorX.value + (pitchTxn.hitLocationX || 0) * fieldScale.value,
      y: homeAnchorY.value + (pitchTxn.hitLocationY || 0) * fieldScale.value,
      relX: pitchTxn.hitLocationX,
      relY: pitchTxn.hitLocationY,
      type: isHit ? 'hit' : 'out',
      trajectory: pitchTxn.battedBallTrajectory || 'G',
    }
  })
})

const getRunnerBase = (id: number) => {
  const br = gameStore.currentBaseRunners
  if (br['1'].some((r) => r.id === id)) return '1'
  if (br['2'].some((r) => r.id === id)) return '2'
  if (br['3'].some((r) => r.id === id)) return '3'
  if (br['B'].some((r) => r.id === id)) return 'B'
  return null
}

const fieldRunners = computed(() => {
  const currentBaseRunners = gameStore.currentBaseRunners
  const runners: Array<{
    id: number
    x: number
    y: number
    uniformNumber?: number | string | null
  }> = []
  const basesMap = {
    '1': firstBaseConfig,
    '2': secondBaseConfig,
    '3': thirdBaseConfig,
    B: homePlateConfig,
  }

  for (const baseKey of ['1', '2', '3', 'B']) {
    const rList = currentBaseRunners[baseKey as keyof typeof currentBaseRunners] || []
    const baseCoords = basesMap[baseKey as keyof typeof basesMap]
    rList.forEach((runner, index) => {
      runners.push({
        ...runner,
        x: baseCoords.value.x + index * 20,
        y: baseCoords.value.y - index * 20,
      })
    })
  }
  return runners
})

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const handleKonvaDragEnd = async (playerId: number | undefined, e: any) => {
  if (playerId === undefined) return
  const x = e.target.x()
  const y = e.target.y()
  const dist = (p1: { x: number; y: number }, p2: { x: number; y: number }) =>
    Math.sqrt(Math.pow(p1.x - p2.x, 2) + Math.pow(p1.y - p2.y, 2))
  const bases = [
    { id: '1', coords: firstBaseConfig.value },
    { id: '2', coords: secondBaseConfig.value },
    { id: '3', coords: thirdBaseConfig.value },
    { id: 'H', coords: homePlateConfig.value },
  ]

  let closestBase: string | null = null
  let minDist = 60
  for (const b of bases) {
    const d = dist({ x, y }, b.coords)
    if (d < minDist) {
      minDist = d
      closestBase = b.id
    }
  }

  if (closestBase === 'H') {
    const br = gameStore.currentBaseRunners
    if (br['B']?.some((r) => r.id === playerId)) closestBase = 'B'
  }

  if (closestBase !== null) await openRunnerSheet(playerId, closestBase)

  const updatedPosition = fieldRunners.value.find((r) => r.id === playerId)
  if (updatedPosition) {
    e.target.x(updatedPosition.x)
    e.target.y(updatedPosition.y)
  }
}
</script>

<style scoped>
.canvas-container {
  border: none;
  border-radius: 0px;
  margin: 0px;
  box-sizing: border-box;
  min-height: 200px;
  overflow: hidden;
}

.border-bottom {
  border-bottom: 1px solid #ccc;
}
.border-primary {
  border-color: var(--q-primary);
}
.border-amber {
  border-color: #ffc107;
}
.border-dashed {
  border-style: dashed !important;
}
.opacity-80 {
  opacity: 0.8;
}

.transition-bg {
  transition:
    background-color 0.3s ease,
    border-color 0.3s ease;
}

.sequence-box {
  border-radius: 14px; /* Slightly rounder */
  border-width: 2px;
  border-style: solid;
  min-width: 32px; /* Shrunk from 44px */
  height: 28px; /* Shrunk from 34px */
  padding: 0 6px;
  font-size: 0.95rem; /* Shrunk from 1.1rem */
  font-weight: 900;
  transition:
    transform 0.15s ease,
    box-shadow 0.15s ease;
}
.sequence-box:active {
  transform: scale(0.95);
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.1) !important;
}

.sequence-list-move {
  transition: transform 0.3s ease;
}
.sequence-list-enter-active,
.sequence-list-leave-active {
  transition: all 0.3s ease;
}
.sequence-list-enter-from,
.sequence-list-leave-to {
  opacity: 0;
  transform: translateX(20px);
}
</style>
